import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { X, Wand2 } from "lucide-react";
import {
  ApiError,
  memoryApi,
  type MemoryEntryOut,
  type MemoryEntryUpdateResponse,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Field, Textarea } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";

function isRuleKind(kind: string): boolean {
  return kind === "user.rule_template" || kind === "skill.rule_template";
}

function readSkeleton(payload: Record<string, unknown>): Record<string, unknown> | null {
  const sk = payload.dsl_skeleton ?? payload.dsl;
  return sk && typeof sk === "object" && !Array.isArray(sk)
    ? (sk as Record<string, unknown>)
    : null;
}

function readTriggers(payload: Record<string, unknown>): string[] {
  const t = payload.trigger_phrases;
  return Array.isArray(t) ? t.map((x) => String(x)) : [];
}

interface Props {
  entry: MemoryEntryOut;
  token: string;
  canEditDirectly: boolean;
  onClose: () => void;
  onSaved: (res: MemoryEntryUpdateResponse) => void;
}

// Side-by-side rule editor: natural language + trigger phrases on the left,
// executable Rule DSL on the right with a deterministic readable preview.
// Non-rule entries fall back to a single natural-language + payload editor.
export function RuleMemoryEditor({
  entry,
  token,
  canEditDirectly,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const rule = isRuleKind(entry.kind);
  const [naturalText, setNaturalText] = useState(entry.natural_text);
  const [triggerText, setTriggerText] = useState(readTriggers(entry.payload).join("\n"));
  const initialDsl = rule ? readSkeleton(entry.payload) : entry.payload;
  const [dslText, setDslText] = useState(JSON.stringify(initialDsl ?? {}, null, 2));
  const [preview, setPreview] = useState<string | null>(entry.rule_description ?? null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [schemaErrors, setSchemaErrors] = useState<string[]>([]);

  function parseDsl(): Record<string, unknown> | null {
    try {
      const parsed = JSON.parse(dslText) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setLocalError("DSL 必须是一个 JSON 对象");
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch {
      setLocalError("DSL 不是合法 JSON");
      return null;
    }
  }

  const describeMutation = useMutation({
    mutationFn: (dsl: Record<string, unknown>) => memoryApi.describeRule(token, dsl),
    onSuccess: (res) => {
      setPreview(res.description);
      setSchemaErrors(res.schema_errors);
    },
    onError: (err: unknown) => {
      setLocalError(err instanceof ApiError ? err.message : "预览失败");
    },
  });

  const saveMutation = useMutation({
    mutationFn: (): Promise<MemoryEntryUpdateResponse> => {
      const body: { natural_text: string; payload: Record<string, unknown> } = {
        natural_text: naturalText.trim(),
        payload: entry.payload,
      };
      if (rule) {
        const dsl = parseDsl();
        if (dsl === null) return Promise.reject(new Error("dsl_parse"));
        const triggers = triggerText
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
        body.payload = { ...entry.payload, dsl_skeleton: dsl, trigger_phrases: triggers };
      } else {
        const parsed = parseDsl();
        if (parsed === null) return Promise.reject(new Error("payload_parse"));
        body.payload = parsed;
      }
      return memoryApi.updateEntry(token, entry.id, body);
    },
    onSuccess: (res) => onSaved(res),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setLocalError(err.message);
        if (err.code === "invalid_rule_dsl") setSchemaErrors(err.message.split("; "));
      } else if (!(err instanceof Error && err.message.endsWith("_parse"))) {
        setLocalError("保存失败");
      }
    },
  });

  function handlePreview(): void {
    setLocalError(null);
    const dsl = parseDsl();
    if (dsl !== null) describeMutation.mutate(dsl);
  }

  function handleSave(): void {
    setLocalError(null);
    setSchemaErrors([]);
    if (!naturalText.trim()) {
      setLocalError("规则描述不能为空");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/30 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex h-[85vh] w-[960px] max-w-[94vw] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="编辑规则"
      >
        <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-[13px] font-medium text-zinc-800">
              {rule ? "编辑规则" : "编辑记忆条目"}
            </span>
            <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px] text-zinc-500">
              {entry.kind}
            </code>
            <span className="text-[11px] text-zinc-400">v{entry.version}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
            title="关闭 (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!canEditDirectly ? (
            <Alert tone="info">
              这是共享规则 (business / skill)，你的修改会提交到审核队列，由管理员批准后才生效。
            </Alert>
          ) : null}
          {localError ? (
            <div className="mt-3">
              <Alert tone="error">{localError}</Alert>
            </div>
          ) : null}

          {rule ? (
            <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-4">
                <Field id="rule-nl" label="规则描述" required hint="用户认可的自然语言口径">
                  <Textarea
                    id="rule-nl"
                    value={naturalText}
                    onChange={(e) => setNaturalText(e.target.value)}
                    rows={3}
                    maxLength={2000}
                  />
                </Field>
                <Field id="rule-triggers" label="触发短语" hint="每行一个，用于未来命中这条规则">
                  <Textarea
                    id="rule-triggers"
                    value={triggerText}
                    onChange={(e) => setTriggerText(e.target.value)}
                    rows={4}
                    placeholder={"营收前三\ntop3"}
                  />
                </Field>
              </div>
              <div className="space-y-4">
                <Field id="rule-dsl" label="规则逻辑 (Rule DSL)" hint="改完点“预览/校验”确认可执行">
                  <Textarea
                    id="rule-dsl"
                    value={dslText}
                    onChange={(e) => setDslText(e.target.value)}
                    rows={10}
                    className="font-mono text-[12px]"
                    spellCheck={false}
                  />
                </Field>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handlePreview}
                    disabled={describeMutation.isPending}
                  >
                    <Wand2 className="mr-1 h-3.5 w-3.5" />
                    {describeMutation.isPending ? "校验中…" : "预览 / 校验"}
                  </Button>
                </div>
                {schemaErrors.length > 0 ? (
                  <Alert tone="error">
                    DSL 不合法：
                    <ul className="mt-1 list-disc pl-5">
                      {schemaErrors.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : preview ? (
                  <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5">
                    <div className="mb-1 text-[11px] font-medium text-zinc-400">
                      这条规则会这样跑：
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-[12px] leading-5 text-zinc-700">
                      {preview}
                    </pre>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-4">
              <Field id="entry-nl" label="描述" required>
                <Textarea
                  id="entry-nl"
                  value={naturalText}
                  onChange={(e) => setNaturalText(e.target.value)}
                  rows={3}
                  maxLength={2000}
                />
              </Field>
              <Field id="entry-payload" label="payload (JSON)">
                <Textarea
                  id="entry-payload"
                  value={dslText}
                  onChange={(e) => setDslText(e.target.value)}
                  rows={8}
                  className="font-mono text-[12px]"
                  spellCheck={false}
                />
              </Field>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between border-t border-zinc-200 px-5 py-3">
          <span className="text-[11px] text-zinc-400">
            confidence {(entry.confidence * 100).toFixed(0)}% · 命中 {entry.hit_count} 次
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              取消
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "保存中…" : canEditDirectly ? "保存" : "提交审核"}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
